import { useState, useEffect, useCallback } from 'react';
import {
  FiUpload, FiX, FiImage, FiTrash2, FiVideo, FiPlus, FiEdit2, FiCheck,
  FiStar, FiMonitor, FiCamera, FiMessageSquare, FiUsers, FiAward, FiBook,
  FiCalendar, FiDollarSign, FiLayout, FiMenu, FiChevronDown, FiGrid,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { settingsAPI } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import {
  ICON_OPTIONS as FACILITY_ICON_OPTIONS,
  COLOR_OPTIONS as FACILITY_COLOR_OPTIONS,
  getFacilityIcon,
  getFacilityColorClasses,
} from '../../utils/facilityOptions';
import { makeUniqueKey } from '../../utils/slug';

// ─── Constants ─────────────────────────────────────────────────────────────────
const emptyTestimonial = { name: '', role: '', rating: 5, text: '' };
const emptyLeader      = { name: '', role: '', description: '', photo: { url: '', publicId: '' } };
const emptyAchievement = { key: '', title: '', desc: '', images: [] };
const emptyFee         = { level: '', admission: '', monthly: '', annual: '' };
const emptyProgram     = { title: '', grades: '', age: '', colorScheme: 'primary', description: '', subjects: [], highlights: [] };
const emptyCalEvent    = { month: '', event: '' };
const COLOR_OPTIONS    = ['blue', 'primary', 'secondary'];
const emptyFacility    = { key: '', title: '', description: '', icon: 'FiMonitor', color: 'primary', images: [] };

// ─── Nav groups ────────────────────────────────────────────────────────────────
const NAV_GROUPS = [
  {
    label: 'Media',
    items: [
      { id: 'logo',   icon: FiImage,         label: 'School Logo',     badge: 'Navbar' },
      { id: 'hero',   icon: FiMonitor,       label: 'Hero Background', badge: 'Homepage' },
      { id: 'campus', icon: FiCamera,        label: 'Campus Photo',    badge: 'About · Homepage' },
      { id: 'popup',  icon: FiLayout,        label: 'Popup Banner',    badge: 'All Pages' },
    ],
  },
  {
    label: 'Homepage',
    items: [
      { id: 'testimonials', icon: FiMessageSquare, label: 'Testimonials', badge: 'Homepage' },
      { id: 'facilities',   icon: FiGrid,          label: 'Facilities', badge: 'Homepage' },
    ],
  },
  {
    label: 'About Page',
    items: [
      { id: 'leadership',   icon: FiUsers, label: 'Leadership Team', badge: 'About' },
      { id: 'achievements', icon: FiAward, label: 'Achievements',    badge: 'About' },
    ],
  },
  {
    label: 'Academics',
    items: [
      { id: 'programs', icon: FiBook,     label: 'Programs', badge: 'Home · Academics' },
      { id: 'calendar', icon: FiCalendar, label: 'Calendar', badge: 'Academics' },
    ],
  },
  {
    label: 'Admissions',
    items: [
      { id: 'fees', icon: FiDollarSign, label: 'Fee Structure', badge: 'Admissions' },
    ],
  },
];

const ALL_ITEMS = NAV_GROUPS.flatMap((g) => g.items);

// ─── Spinner ───────────────────────────────────────────────────────────────────
const Spinner = ({ size = 'sm', color = 'white' }) => {
  const sz = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const cl = color === 'white' ? 'border-white border-t-transparent' : 'border-red-400 border-t-transparent';
  return <div className={`${sz} border-2 ${cl} rounded-full animate-spin`} />;
};

// ─── Save button ───────────────────────────────────────────────────────────────
const SaveBtn = ({ saving, onClick, disabled, label = 'Save Changes' }) => (
  <button
    onClick={onClick}
    disabled={saving || disabled}
    className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-white rounded-xl text-sm font-medium hover:bg-primary-600 disabled:opacity-60 transition-colors shadow-sm"
  >
    {saving ? <Spinner /> : <FiCheck className="w-3.5 h-3.5" />}
    {saving ? 'Saving…' : label}
  </button>
);

// ─── Card wrapper ──────────────────────────────────────────────────────────────
const Card = ({ children, className = '' }) => (
  <div className={`bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-200 p-5 sm:p-6 ${className}`}>
    {children}
  </div>
);

// ─── Section header inside a card ─────────────────────────────────────────────
const CardHeader = ({ title, subtitle, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
    <div>
      <h3 className="text-base sm:text-lg font-semibold text-gray-800">{title}</h3>
      {subtitle && <p className="text-gray-500 text-sm mt-0.5">{subtitle}</p>}
    </div>
    {action && <div className="flex-shrink-0">{action}</div>}
  </div>
);

// ─── Upload drop-zone ──────────────────────────────────────────────────────────
const DropZone = ({ onChange, accept, icon: Icon = FiImage, label, hint, disabled }) => (
  <label className={`flex flex-col items-center justify-center w-full h-36 border-2 border-dashed rounded-2xl cursor-pointer transition-colors
    ${disabled ? 'opacity-50 cursor-not-allowed border-gray-200' : 'border-gray-200 hover:border-primary-400 hover:bg-primary-50/30'}`}>
    <Icon className="w-8 h-8 text-gray-300 mb-2" />
    <span className="text-gray-600 text-sm font-medium">{label}</span>
    <span className="text-gray-400 text-xs mt-1">{hint}</span>
    <input type="file" onChange={onChange} className="hidden" accept={accept} disabled={disabled} />
  </label>
);

// ─── Image preview with remove ────────────────────────────────────────────────
const ImagePreviewBox = ({ src, alt, onClear, label, isVideo = false, height = 'h-48' }) => (
  <div className={`relative w-full ${height} rounded-2xl overflow-hidden border border-gray-200 bg-black`}>
    {isVideo
      ? <video src={src} className="w-full h-full object-cover" controls muted />
      : <img src={src} alt={alt} className="w-full h-full object-cover" />}
    {onClear && (
      <button
        onClick={onClear}
        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 shadow transition-colors"
      >
        <FiX className="w-3.5 h-3.5" />
      </button>
    )}
    {label && (
      <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-lg">
        {label}
      </div>
    )}
  </div>
);

// ─── Facility images card ──────────────────────────────────────────────────────
const FacilityImageCard = ({ def, images, uploading, deletingMap, onUpload, onRemove }) => {
  const inputId = `facility-upload-${def.key}`;
  return (
    <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className="font-medium text-gray-800 text-sm">{def.title}</p>
        <label
          htmlFor={inputId}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border cursor-pointer transition-colors flex-shrink-0
            ${uploading ? 'opacity-60 pointer-events-none border-gray-200 text-gray-400' : 'border-primary-200 text-primary-600 hover:bg-primary-50'}`}
        >
          {uploading ? <Spinner /> : <FiPlus className="w-3.5 h-3.5" />}
          {uploading ? 'Uploading…' : 'Add Photos'}
        </label>
        <input
          id={inputId}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          disabled={uploading}
          onChange={(e) => { onUpload(def.key, e.target.files); e.target.value = ''; }}
        />
      </div>
      {images.length === 0 ? (
        <p className="text-gray-400 text-xs py-4 text-center">No photos yet.</p>
      ) : (
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
          {images.map((img) => {
            const dKey = `${def.key}:${img.publicId}`;
            const isDeleting = !!deletingMap[dKey];
            return (
              <div key={img.publicId} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 group">
                <img src={img.url} alt={def.title} className="w-full h-full object-cover" />
                <button
                  onClick={() => onRemove(def.key, img.publicId)}
                  disabled={isDeleting}
                  className="absolute top-1 right-1 p-1 bg-red-500/90 text-white rounded-full opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity disabled:opacity-60"
                >
                  {isDeleting ? <Spinner /> : <FiX className="w-3 h-3" />}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Facility metadata form (add / edit) ──────────────────────────────────────
const FacilityForm = ({ form, setForm, onCommit, onCancel, busy }) => (
  <div className="bg-primary-50 border border-primary-100 rounded-xl p-4 space-y-3">
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
      <input type="text" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 transition-shadow" placeholder="e.g. Music Room" />
    </div>
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
      <textarea rows={2} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 transition-shadow resize-none" placeholder="Short description shown on the card…" />
    </div>
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-2">Icon</label>
      <div className="flex flex-wrap gap-2">
        {FACILITY_ICON_OPTIONS.map(({ key, label, Icon }) => (
          <button key={key} type="button" title={label} onClick={() => setForm((p) => ({ ...p, icon: key }))}
            className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-colors
              ${form.icon === key ? 'border-primary-500 bg-primary-100 text-primary-600' : 'border-gray-200 text-gray-400 hover:border-gray-300 bg-white'}`}>
            <Icon className="w-4 h-4" />
          </button>
        ))}
      </div>
    </div>
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-2">Color</label>
      <div className="flex flex-wrap gap-2">
        {FACILITY_COLOR_OPTIONS.map(({ key, label, swatch }) => (
          <button key={key} type="button" title={label} onClick={() => setForm((p) => ({ ...p, color: key }))}
            className={`w-7 h-7 rounded-full ${swatch} ring-2 ring-offset-2 transition-all ${form.color === key ? 'ring-primary-500' : 'ring-transparent'}`} />
        ))}
      </div>
    </div>
    <div className="flex gap-2 pt-1">
      <button onClick={onCommit} disabled={busy}
        className="flex items-center gap-1.5 px-4 py-1.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-60 transition-colors">
        {busy && <Spinner />}
        {busy ? 'Saving…' : 'Done'}
      </button>
      <button onClick={onCancel} disabled={busy} className="px-4 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors disabled:opacity-60">Cancel</button>
    </div>
  </div>
);

// ─── Facilities manager (add / edit / delete + per-card photo gallery) ────────
const FacilitiesManager = ({ facilities, onAdd, onEdit, onRemove, facilityUploading, facilityDeleting, onUploadImages, onRemoveImage }) => {
  const [editing, setEditing]         = useState(null); // 'new' | index | null
  const [form, setForm]               = useState(emptyFacility);
  const [busy, setBusy]               = useState(false);
  const [removingIdx, setRemovingIdx] = useState(null);

  const startEdit = (idx) => { setEditing(idx); setForm({ ...facilities[idx] }); };
  const startNew  = () => { setEditing('new'); setForm({ ...emptyFacility }); };
  const cancel    = () => { setEditing(null); setForm(emptyFacility); };

  const commit = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setBusy(true);
    try {
      if (editing === 'new') await onAdd(form);
      else await onEdit(editing, form);
      setEditing(null); setForm(emptyFacility);
    } finally { setBusy(false); }
  };

  const remove = async (idx) => {
    if (!window.confirm(`Remove "${facilities[idx].title}"? Its uploaded photos will be deleted too.`)) return;
    setRemovingIdx(idx);
    try { await onRemove(idx); } finally { setRemovingIdx(null); }
  };

  return (
    <Card>
      <CardHeader
        title="Facilities"
        subtitle="Shown in the homepage 'State-of-the-Art Infrastructure' section. Add, edit, or remove cards, and manage each one's photos below."
      />
      <div className="space-y-3">
        {facilities.length === 0 && editing !== 'new' && (
          <p className="text-gray-400 text-sm text-center py-6">No facilities yet. Add one below.</p>
        )}
        {facilities.map((facility, idx) => {
          if (editing === idx) {
            return <FacilityForm key={facility.key} form={form} setForm={setForm} onCommit={commit} onCancel={cancel} busy={busy} />;
          }
          const Icon = getFacilityIcon(facility.icon);
          return (
            <div key={facility.key} className="bg-gray-50 border border-gray-100 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center border flex-shrink-0 ${getFacilityColorClasses(facility.color)}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-800 text-sm truncate">{facility.title}</p>
                    {facility.description && <p className="text-gray-500 text-xs truncate mt-0.5">{facility.description}</p>}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(idx)} disabled={removingIdx === idx} className="p-2 text-gray-400 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50">
                    <FiEdit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(idx)} disabled={removingIdx === idx} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50">
                    {removingIdx === idx ? <Spinner color="red" /> : <FiTrash2 className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <FacilityImageCard
                def={facility}
                images={facility.images || []}
                uploading={!!facilityUploading[facility.key]}
                deletingMap={facilityDeleting}
                onUpload={onUploadImages}
                onRemove={onRemoveImage}
              />
            </div>
          );
        })}
        {editing === 'new' && <FacilityForm form={form} setForm={setForm} onCommit={commit} onCancel={cancel} busy={busy} />}
      </div>
      {editing === null && (
        <button onClick={startNew} className="mt-4 flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-700 font-medium transition-colors">
          <FiPlus className="w-4 h-4" /> Add Facility
        </button>
      )}
    </Card>
  );
};

// ─── Achievement photo grid (with per-photo caption) ──────────────────────────
const AchievementImageCard = ({ achievementKey, images, uploading, deletingMap, onUpload, onRemove, onCaptionSave }) => {
  const inputId = `achievement-upload-${achievementKey}`;
  return (
    <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Photos</p>
        <label
          htmlFor={inputId}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border cursor-pointer transition-colors flex-shrink-0
            ${uploading ? 'opacity-60 pointer-events-none border-gray-200 text-gray-400' : 'border-primary-200 text-primary-600 hover:bg-primary-50'}`}
        >
          {uploading ? <Spinner /> : <FiPlus className="w-3.5 h-3.5" />}
          {uploading ? 'Uploading…' : 'Add Photos'}
        </label>
        <input
          id={inputId}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          disabled={uploading}
          onChange={(e) => { onUpload(achievementKey, e.target.files); e.target.value = ''; }}
        />
      </div>
      {images.length === 0 ? (
        <p className="text-gray-400 text-xs py-3 text-center">No photos yet.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
          {images.map((img) => {
            const isDeleting = !!deletingMap[img._id];
            return (
              <div key={img._id} className="space-y-1">
                <div className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 group">
                  <img src={img.url} alt={img.caption || ''} className="w-full h-full object-cover" />
                  <button
                    onClick={() => onRemove(achievementKey, img._id)}
                    disabled={isDeleting}
                    className="absolute top-1 right-1 p-1 bg-red-500/90 text-white rounded-full opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity disabled:opacity-60"
                  >
                    {isDeleting ? <Spinner /> : <FiX className="w-3 h-3" />}
                  </button>
                </div>
                <input
                  type="text"
                  defaultValue={img.caption || ''}
                  placeholder="Name — GPA"
                  onBlur={(e) => onCaptionSave(achievementKey, img._id, e.target.value)}
                  className="w-full px-1.5 py-1 border border-gray-200 rounded text-[10px] focus:outline-none focus:ring-1 focus:ring-primary-300"
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Achievement metadata form (add / edit) ───────────────────────────────────
const AchievementForm = ({ form, setForm, onCommit, onCancel, busy }) => (
  <div className="bg-primary-50 border border-primary-100 rounded-xl p-4 space-y-3">
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
      <input type="text" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 transition-shadow" placeholder="e.g. Best School of the Year 2025" />
    </div>
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
      <textarea rows={2} value={form.desc} onChange={(e) => setForm((p) => ({ ...p, desc: e.target.value }))}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 transition-shadow resize-none" placeholder="e.g. Awarded by Nepal Government" />
    </div>
    <div className="flex gap-2 pt-1">
      <button onClick={onCommit} disabled={busy}
        className="flex items-center gap-1.5 px-4 py-1.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-60 transition-colors">
        {busy && <Spinner />}
        {busy ? 'Saving…' : 'Done'}
      </button>
      <button onClick={onCancel} disabled={busy} className="px-4 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors disabled:opacity-60">Cancel</button>
    </div>
  </div>
);

// ─── Achievements manager (add / edit / delete + per-item photo gallery) ──────
const AchievementsManager = ({ achievements, onAdd, onEdit, onRemove, achievementUploading, achievementDeleting, onUploadImages, onRemoveImage, onCaptionSave }) => {
  const [editing, setEditing]         = useState(null); // 'new' | index | null
  const [form, setForm]               = useState(emptyAchievement);
  const [busy, setBusy]               = useState(false);
  const [removingIdx, setRemovingIdx] = useState(null);

  const startEdit = (idx) => { setEditing(idx); setForm({ ...achievements[idx] }); };
  const startNew  = () => { setEditing('new'); setForm({ ...emptyAchievement }); };
  const cancel    = () => { setEditing(null); setForm(emptyAchievement); };

  const commit = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setBusy(true);
    try {
      if (editing === 'new') await onAdd(form);
      else await onEdit(editing, form);
      setEditing(null); setForm(emptyAchievement);
    } finally { setBusy(false); }
  };

  const remove = async (idx) => {
    if (!window.confirm(`Remove "${achievements[idx].title}"? Its uploaded photos will be deleted too.`)) return;
    setRemovingIdx(idx);
    try { await onRemove(idx); } finally { setRemovingIdx(null); }
  };

  return (
    <Card>
      <CardHeader
        title="Achievements"
        subtitle="Shown in the About page 'Achievements & Recognition' section. Add photos under each achievement — e.g. individual student photos with their name and GPA."
      />
      <div className="space-y-3">
        {achievements.length === 0 && editing !== 'new' && (
          <p className="text-gray-400 text-sm text-center py-6">No achievements yet. Add one below.</p>
        )}
        {achievements.map((achievement, idx) => {
          if (editing === idx) {
            return <AchievementForm key={achievement.key} form={form} setForm={setForm} onCommit={commit} onCancel={cancel} busy={busy} />;
          }
          return (
            <div key={achievement.key} className="bg-gray-50 border border-gray-100 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <p className="font-medium text-gray-800 text-sm truncate">{achievement.title}</p>
                  {achievement.desc && <p className="text-gray-500 text-xs truncate mt-0.5">{achievement.desc}</p>}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(idx)} disabled={removingIdx === idx} className="p-2 text-gray-400 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50">
                    <FiEdit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(idx)} disabled={removingIdx === idx} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50">
                    {removingIdx === idx ? <Spinner color="red" /> : <FiTrash2 className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <AchievementImageCard
                achievementKey={achievement.key}
                images={achievement.images || []}
                uploading={!!achievementUploading[achievement.key]}
                deletingMap={achievementDeleting}
                onUpload={onUploadImages}
                onRemove={onRemoveImage}
                onCaptionSave={onCaptionSave}
              />
            </div>
          );
        })}
        {editing === 'new' && <AchievementForm form={form} setForm={setForm} onCommit={commit} onCancel={cancel} busy={busy} />}
      </div>
      {editing === null && (
        <button onClick={startNew} className="mt-4 flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-700 font-medium transition-colors">
          <FiPlus className="w-4 h-4" /> Add Achievement
        </button>
      )}
    </Card>
  );
};

// ─── Star picker ───────────────────────────────────────────────────────────────
const StarPicker = ({ value, onChange }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((s) => (
      <button key={s} type="button" onClick={() => onChange(s)} className="focus:outline-none">
        <FiStar
          className={`w-5 h-5 transition-colors ${s <= value ? 'text-amber-400' : 'text-gray-300'}`}
          style={{ fill: s <= value ? '#f59e0b' : 'none' }}
        />
      </button>
    ))}
  </div>
);

// ─── Form row (inline edit) ────────────────────────────────────────────────────
const inputCls = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 transition-shadow';

const FormRow = ({ fields, form, setForm, onCommit, onCancel }) => (
  <div className="bg-primary-50 border border-primary-100 rounded-xl p-4 space-y-3">
    {fields.map((f) => (
      <div key={f.key}>
        <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}{f.required ? ' *' : ''}</label>
        {f.type === 'textarea' ? (
          <textarea rows={3} value={form[f.key] || ''} onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
            className={`${inputCls} resize-none`} placeholder={f.placeholder || ''} />
        ) : f.type === 'stars' ? (
          <StarPicker value={form[f.key] || 5} onChange={(v) => setForm((p) => ({ ...p, [f.key]: v }))} />
        ) : (
          <input type="text" value={form[f.key] || ''} onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
            className={inputCls} placeholder={f.placeholder || ''} />
        )}
      </div>
    ))}
    <div className="flex gap-2 pt-1">
      <button onClick={onCommit} className="px-4 py-1.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors">Done</button>
      <button onClick={onCancel} className="px-4 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors">Cancel</button>
    </div>
  </div>
);

// ─── Generic CRUD section ──────────────────────────────────────────────────────
const CrudSection = ({ title, subtitle, items, setItems, emptyItem, fields, saving, onSave }) => {
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(emptyItem);

  const startEdit = (idx) => { setEditing(idx); setForm({ ...items[idx] }); };
  const startNew  = () => { setEditing('new'); setForm({ ...emptyItem }); };
  const cancel    = () => { setEditing(null); setForm(emptyItem); };

  const commit = () => {
    const firstKey = fields[0].key;
    if (!form[firstKey]?.toString().trim()) { toast.error(`${fields[0].label} is required`); return; }
    const updated = editing === 'new' ? [...items, form] : items.map((it, i) => i === editing ? form : it);
    setItems(updated);
    setEditing(null);
    setForm(emptyItem);
  };

  const remove = (idx) => {
    if (!window.confirm('Remove this item?')) return;
    setItems(items.filter((_, i) => i !== idx));
  };

  return (
    <Card>
      <CardHeader title={title} subtitle={subtitle} action={<SaveBtn saving={saving} onClick={onSave} />} />
      <div className="space-y-2">
        {items.length === 0 && editing !== 'new' && (
          <p className="text-gray-400 text-sm text-center py-6">No items yet. Add one below.</p>
        )}
        {items.map((item, idx) =>
          editing === idx ? (
            <FormRow key={idx} fields={fields} form={form} setForm={setForm} onCommit={commit} onCancel={cancel} />
          ) : (
            <div key={idx} className="flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 text-sm truncate">{item[fields[0].key]}</p>
                {fields[1] && <p className="text-gray-500 text-xs truncate mt-0.5">{item[fields[1].key]}</p>}
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => startEdit(idx)} className="p-2 text-gray-400 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors">
                  <FiEdit2 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => remove(idx)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                  <FiTrash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )
        )}
        {editing === 'new' && <FormRow fields={fields} form={form} setForm={setForm} onCommit={commit} onCancel={cancel} />}
      </div>
      {editing === null && (
        <button onClick={startNew} className="mt-4 flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-700 font-medium transition-colors">
          <FiPlus className="w-4 h-4" /> Add {title.replace(/s$/, '')}
        </button>
      )}
    </Card>
  );
};

// ─── Leadership section ────────────────────────────────────────────────────────
const getInitials = (name) =>
  name ? name.split(' ').filter(Boolean).map((n) => n[0]).join('').toUpperCase().slice(0, 2) : '??';

const LeaderForm = ({ form, setForm, photoPreview, onPhotoChange, uploadingPhoto, onCommit, onCancel }) => (
  <div className="bg-primary-50 border border-primary-100 rounded-xl p-4 space-y-3">
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-2">Photo</label>
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 border-2 border-gray-200">
          {photoPreview ? (
            <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
          ) : form.photo?.url ? (
            <img src={form.photo.url} alt="Current" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
              <FiImage className="w-5 h-5 text-gray-400" />
            </div>
          )}
        </div>
        <label className="cursor-pointer flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-white transition-colors text-sm text-gray-500">
          <FiUpload className="w-4 h-4" />
          {photoPreview || form.photo?.url ? 'Change photo' : 'Upload photo'}
          <input type="file" accept="image/*" onChange={onPhotoChange} className="hidden" />
        </label>
      </div>
    </div>
    {[
      { key: 'name',        label: 'Full Name *',  placeholder: 'e.g. Dr. Ramesh Kumar Sharma' },
      { key: 'role',        label: 'Position *',   placeholder: 'e.g. Principal' },
    ].map((f) => (
      <div key={f.key}>
        <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
        <input type="text" value={form[f.key] || ''} onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
          className={inputCls} placeholder={f.placeholder} />
      </div>
    ))}
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
      <textarea rows={2} value={form.description || ''} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
        className={`${inputCls} resize-none`} placeholder="Brief bio…" />
    </div>
    <div className="flex gap-2 pt-1">
      <button onClick={onCommit} disabled={uploadingPhoto}
        className="flex items-center gap-1.5 px-4 py-1.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-60 transition-colors">
        {uploadingPhoto && <Spinner />}
        {uploadingPhoto ? 'Uploading…' : 'Done'}
      </button>
      <button onClick={onCancel} className="px-4 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors">Cancel</button>
    </div>
  </div>
);

const LeadershipSection = ({ leadership, setLeadership, saving, onSave }) => {
  const [editing,        setEditing]        = useState(null);
  const [form,           setForm]           = useState({ ...emptyLeader });
  const [photoFile,      setPhotoFile]      = useState(null);
  const [photoPreview,   setPhotoPreview]   = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const startEdit = (idx) => { setEditing(idx); setForm({ ...leadership[idx] }); setPhotoFile(null); setPhotoPreview(null); };
  const startNew  = () => { setEditing('new'); setForm({ ...emptyLeader }); setPhotoFile(null); setPhotoPreview(null); };
  const cancel    = () => { setEditing(null); setPhotoFile(null); if (photoPreview) URL.revokeObjectURL(photoPreview); setPhotoPreview(null); };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const commit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.role.trim()) { toast.error('Role is required'); return; }
    let photoData = form.photo || { url: '', publicId: '' };
    if (photoFile) {
      setUploadingPhoto(true);
      try {
        const fd = new FormData();
        fd.append('photo', photoFile);
        if (photoData.publicId) fd.append('oldPublicId', photoData.publicId);
        const res = await settingsAPI.uploadLeadershipPhoto(fd);
        photoData = res.data?.data?.photo || photoData;
      } catch { toast.error('Photo upload failed'); setUploadingPhoto(false); return; }
      finally   { setUploadingPhoto(false); }
    }
    const updated = editing === 'new'
      ? [...leadership, { ...form, photo: photoData }]
      : leadership.map((it, i) => i === editing ? { ...form, photo: photoData } : it);
    setLeadership(updated);
    setEditing(null);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const remove = (idx) => {
    if (!window.confirm('Remove this member?')) return;
    setLeadership(leadership.filter((_, i) => i !== idx));
  };

  return (
    <Card>
      <CardHeader title="Leadership Team" subtitle="Shown in 'Meet the Administration' on the About page." action={<SaveBtn saving={saving} onClick={onSave} />} />
      <div className="space-y-2">
        {leadership.length === 0 && editing !== 'new' && <p className="text-gray-400 text-sm text-center py-6">No members yet.</p>}
        {leadership.map((person, idx) =>
          editing === idx ? (
            <LeaderForm key={idx} form={form} setForm={setForm} photoPreview={photoPreview} onPhotoChange={handlePhotoChange} uploadingPhoto={uploadingPhoto} onCommit={commit} onCancel={cancel} />
          ) : (
            <div key={idx} className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                {person.photo?.url
                  ? <img src={person.photo.url} alt={person.name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full bg-primary-500 flex items-center justify-center text-white text-sm font-bold">{getInitials(person.name)}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 text-sm truncate">{person.name}</p>
                <p className="text-gray-500 text-xs truncate">{person.role}</p>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => startEdit(idx)} className="p-2 text-gray-400 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"><FiEdit2 className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(idx)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><FiTrash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          )
        )}
        {editing === 'new' && <LeaderForm form={form} setForm={setForm} photoPreview={photoPreview} onPhotoChange={handlePhotoChange} uploadingPhoto={uploadingPhoto} onCommit={commit} onCancel={cancel} />}
      </div>
      {editing === null && (
        <button onClick={startNew} className="mt-4 flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-700 font-medium transition-colors">
          <FiPlus className="w-4 h-4" /> Add Member
        </button>
      )}
    </Card>
  );
};

// ─── Programs section ──────────────────────────────────────────────────────────
const ProgramForm = ({ form, setForm, onCommit, onCancel }) => (
  <div className="bg-primary-50 border border-primary-100 rounded-xl p-4 space-y-3">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
        <input type="text" value={form.title || ''} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
          className={inputCls} placeholder="e.g. Primary School" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Color Theme</label>
        <select value={form.colorScheme || 'primary'} onChange={(e) => setForm(p => ({ ...p, colorScheme: e.target.value }))}
          className={`${inputCls} bg-white`}>
          {COLOR_OPTIONS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Grades</label>
        <input type="text" value={form.grades || ''} onChange={(e) => setForm(p => ({ ...p, grades: e.target.value }))}
          className={inputCls} placeholder="e.g. Grade 1 - 5" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Age Range</label>
        <input type="text" value={form.age || ''} onChange={(e) => setForm(p => ({ ...p, age: e.target.value }))}
          className={inputCls} placeholder="e.g. Ages 6-11" />
      </div>
    </div>
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
      <textarea rows={3} value={form.description || ''} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
        className={`${inputCls} resize-none`} placeholder="Program description…" />
    </div>
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">Subjects <span className="text-gray-400 font-normal">(one per line)</span></label>
      <textarea rows={4} value={(form.subjects || []).join('\n')} onChange={(e) => setForm(p => ({ ...p, subjects: e.target.value.split('\n') }))}
        className={`${inputCls} resize-none`} placeholder={"Mathematics\nScience\nEnglish"} />
    </div>
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">Highlights <span className="text-gray-400 font-normal">(one per line)</span></label>
      <textarea rows={3} value={(form.highlights || []).join('\n')} onChange={(e) => setForm(p => ({ ...p, highlights: e.target.value.split('\n') }))}
        className={`${inputCls} resize-none`} placeholder={"Small class sizes\nExperienced teachers"} />
    </div>
    <div className="flex gap-2 pt-1">
      <button onClick={onCommit} className="px-4 py-1.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors">Done</button>
      <button onClick={onCancel} className="px-4 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors">Cancel</button>
    </div>
  </div>
);

const ProgramsSection = ({ programs, setPrograms, saving, onSave }) => {
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState({ ...emptyProgram });

  const startEdit = (idx) => { setEditing(idx); setForm({ ...programs[idx], subjects: programs[idx].subjects || [], highlights: programs[idx].highlights || [] }); };
  const startNew  = () => { setEditing('new'); setForm({ ...emptyProgram }); };
  const cancel    = () => setEditing(null);

  const commit = () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    const cleaned = { ...form, subjects: (form.subjects || []).map(s => s.trim()).filter(Boolean), highlights: (form.highlights || []).map(s => s.trim()).filter(Boolean) };
    const updated = editing === 'new' ? [...programs, cleaned] : programs.map((p, i) => i === editing ? cleaned : p);
    setPrograms(updated);
    setEditing(null);
  };

  const remove = (idx) => {
    if (!window.confirm('Remove this program?')) return;
    setPrograms(programs.filter((_, i) => i !== idx));
  };

  const colorDot = (scheme) => scheme === 'blue' ? 'bg-blue-500' : scheme === 'secondary' ? 'bg-secondary-500' : 'bg-primary-500';

  return (
    <Card>
      <CardHeader title="Academic Programs" subtitle="Shown on the Home and Academics pages." action={<SaveBtn saving={saving} onClick={onSave} />} />
      <div className="space-y-2">
        {programs.length === 0 && editing !== 'new' && <p className="text-gray-400 text-sm text-center py-6">No programs yet.</p>}
        {programs.map((prog, idx) =>
          editing === idx ? (
            <ProgramForm key={idx} form={form} setForm={setForm} onCommit={commit} onCancel={cancel} />
          ) : (
            <div key={idx} className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors">
              <div className={`w-3 h-3 rounded-full flex-shrink-0 ${colorDot(prog.colorScheme)}`} />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 text-sm truncate">{prog.title}</p>
                <p className="text-gray-500 text-xs">{prog.grades}{prog.age ? ` · ${prog.age}` : ''}</p>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => startEdit(idx)} className="p-2 text-gray-400 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"><FiEdit2 className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(idx)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><FiTrash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          )
        )}
        {editing === 'new' && <ProgramForm form={form} setForm={setForm} onCommit={commit} onCancel={cancel} />}
      </div>
      {editing === null && (
        <button onClick={startNew} className="mt-4 flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-700 font-medium transition-colors">
          <FiPlus className="w-4 h-4" /> Add Program
        </button>
      )}
    </Card>
  );
};

// ─── Mobile nav dropdown ───────────────────────────────────────────────────────
const MobileNav = ({ activeSection, onSelect }) => {
  const [open, setOpen] = useState(false);
  const activeItem = ALL_ITEMS.find((i) => i.id === activeSection);
  const Icon = activeItem?.icon || FiMenu;

  return (
    <div className="relative lg:hidden mb-4">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-white border border-gray-200 rounded-xl shadow-sm text-sm font-medium text-gray-700 hover:border-primary-300 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary-500" />
          <span>{activeItem?.label || 'Select section'}</span>
          <span className="text-xs text-gray-400 font-normal hidden sm:inline">· {activeItem?.badge}</span>
        </div>
        <FiChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
          {NAV_GROUPS.map((group, gi) => (
            <div key={group.label} className={gi > 0 ? 'border-t border-gray-100' : ''}>
              <p className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-widest">{group.label}</p>
              {group.items.map((item) => {
                const ItemIcon = item.icon;
                const active = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => { onSelect(item.id); setOpen(false); }}
                    className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left transition-colors
                      ${active ? 'bg-primary-50 text-primary-600 font-semibold' : 'text-gray-600 hover:bg-gray-50'}`}
                  >
                    <ItemIcon className={`w-4 h-4 ${active ? 'text-primary-500' : 'text-gray-400'}`} />
                    {item.label}
                    <span className="ml-auto text-xs text-gray-400">{item.badge}</span>
                  </button>
                );
              })}
            </div>
          ))}
          <div className="pb-1" />
        </div>
      )}
    </div>
  );
};

// ─── Breadcrumb ────────────────────────────────────────────────────────────────
const SectionBreadcrumb = ({ activeSection }) => {
  const item = ALL_ITEMS.find((i) => i.id === activeSection);
  if (!item) return null;
  const Icon = item.icon;
  return (
    <div className="flex items-center gap-2 mb-4 text-sm">
      <Icon className="w-4 h-4 text-primary-500 flex-shrink-0" />
      <span className="font-semibold text-gray-700">{item.label}</span>
      <span className="text-gray-300">·</span>
      <span className="text-xs text-gray-400">Appears on: <span className="font-medium text-gray-500">{item.badge}</span></span>
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────
const AdminSettings = () => {
  const [loading,        setLoading]        = useState(true);
  const [activeSection,  setActiveSection]  = useState('hero');

  // Logo
  const [logoUrl,     setLogoUrl]     = useState('');
  const [logoFile,    setLogoFile]    = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [savingLogo,  setSavingLogo]  = useState(false);
  const [removingLogo,setRemovingLogo]= useState(false);

  // Campus
  const [campusImageUrl, setCampusImageUrl] = useState('');
  const [campusFile,     setCampusFile]     = useState(null);
  const [campusPreview,  setCampusPreview]  = useState(null);
  const [savingCampus,   setSavingCampus]   = useState(false);

  // Hero
  const [heroMedia,    setHeroMedia]    = useState({ mediaType: 'none', url: '' });
  const [heroMediaType,setHeroMediaType]= useState('image');
  const [heroFile,     setHeroFile]     = useState(null);
  const [heroPreview,  setHeroPreview]  = useState(null);
  const [savingHero,   setSavingHero]   = useState(false);
  const [removingHero, setRemovingHero] = useState(false);

  // Popup
  const [popupBannerUrl, setPopupBannerUrl] = useState('');
  const [popupFile,      setPopupFile]      = useState(null);
  const [popupPreview,   setPopupPreview]   = useState(null);
  const [savingPopup,    setSavingPopup]    = useState(false);
  const [removingPopup,  setRemovingPopup]  = useState(false);

  // Content
  const [testimonials,    setTestimonials]    = useState([]);
  const [leadership,      setLeadership]      = useState([]);
  const [achievements,    setAchievements]    = useState([]);
  const [feeStructure,    setFeeStructure]    = useState([]);
  const [programs,        setPrograms]        = useState([]);
  const [academicCalendar,setAcademicCalendar]= useState([]);

  const [savingTestimonials, setSavingTestimonials] = useState(false);
  const [savingLeadership,   setSavingLeadership]   = useState(false);
  const [savingFees,         setSavingFees]         = useState(false);
  const [savingPrograms,     setSavingPrograms]      = useState(false);
  const [savingCalendar,     setSavingCalendar]      = useState(false);

  // Facilities
  const [facilities,         setFacilities]        = useState([]);
  const [facilityUploading,  setFacilityUploading] = useState({});
  const [facilityDeleting,   setFacilityDeleting]  = useState({});

  // Achievements
  const [achievementUploading, setAchievementUploading] = useState({});
  const [achievementDeleting,  setAchievementDeleting]  = useState({});

  const loadSettings = (showLoader = false) => {
    if (showLoader) setLoading(true);
    return settingsAPI.get().then((res) => {
      const s = res.data?.data?.settings;
      if (!s) return;
      if (s?.logo?.url) setLogoUrl(s.logo.url);
      if (s?.campusImage?.url) setCampusImageUrl(s.campusImage.url);
      if (s?.heroMedia?.mediaType && s.heroMedia.mediaType !== 'none') {
        setHeroMedia(s.heroMedia);
        setHeroMediaType(s.heroMedia.mediaType);
      }
      if (s?.popupBanner?.url) setPopupBannerUrl(s.popupBanner.url);
      setTestimonials(s.testimonials || []);
      setLeadership(s.leadership || []);
      setAchievements(s.achievements || []);
      setFeeStructure(s.feeStructure || []);
      setPrograms(s.programs || []);
      setAcademicCalendar(s.academicCalendar || []);
      setFacilities(s.facilities || []);
    }).catch(() => {
      toast.error('Failed to load settings. Please refresh the page.');
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadSettings(true); }, []);

  // ── Logo ──
  const handleLogoFileChange = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };
  const handleLogoUpload = async () => {
    if (!logoFile) { toast.error('Please select an image'); return; }
    setSavingLogo(true);
    try {
      const fd = new FormData(); fd.append('image', logoFile);
      const res = await settingsAPI.updateLogo(fd);
      const url = res.data?.data?.settings?.logo?.url;
      if (url) setLogoUrl(url);
      setLogoFile(null); setLogoPreview(null);
      toast.success('School logo updated');
    } catch (err) { toast.error(err.response?.data?.message || 'Upload failed'); }
    finally { setSavingLogo(false); }
  };
  const handleLogoRemove = async () => {
    if (!window.confirm('Remove the school logo?')) return;
    setRemovingLogo(true);
    try {
      await settingsAPI.removeLogo();
      setLogoUrl(''); setLogoFile(null); setLogoPreview(null);
      toast.success('Logo removed');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to remove'); }
    finally { setRemovingLogo(false); }
  };

  // ── Campus ──
  const handleCampusFileChange = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setCampusFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setCampusPreview(ev.target.result);
    reader.readAsDataURL(file);
  };
  const handleCampusUpload = async () => {
    if (!campusFile) { toast.error('Please select an image'); return; }
    setSavingCampus(true);
    try {
      const fd = new FormData(); fd.append('image', campusFile);
      const res = await settingsAPI.updateCampusImage(fd);
      const url = res.data?.data?.settings?.campusImage?.url;
      if (url) setCampusImageUrl(url);
      setCampusFile(null); setCampusPreview(null);
      toast.success('Campus photo updated');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to upload'); }
    finally { setSavingCampus(false); }
  };

  // ── Hero ──
  const handleHeroFileChange = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setHeroFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setHeroPreview({ type: 'image', src: ev.target.result });
      reader.readAsDataURL(file);
    } else {
      setHeroPreview({ type: 'video', src: URL.createObjectURL(file) });
    }
  };
  const handleHeroUpload = async () => {
    if (!heroFile) { toast.error('Please select a file'); return; }
    setSavingHero(true);
    try {
      const fd = new FormData(); fd.append('media', heroFile);
      const res = await settingsAPI.updateHeroMedia(fd);
      const media = res.data?.data?.settings?.heroMedia;
      if (media) setHeroMedia(media);
      setHeroFile(null); setHeroPreview(null);
      toast.success('Hero background updated');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to upload. Videos must be MP4/WebM under 100MB.'); }
    finally { setSavingHero(false); }
  };
  const handleHeroRemove = async () => {
    setRemovingHero(true);
    try {
      await settingsAPI.removeHeroMedia();
      setHeroMedia({ mediaType: 'none', url: '' }); setHeroFile(null); setHeroPreview(null);
      toast.success('Hero background removed');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to remove'); }
    finally { setRemovingHero(false); }
  };
  const clearHeroSelection = useCallback(() => {
    setHeroFile(null);
    if (heroPreview?.type === 'video') URL.revokeObjectURL(heroPreview.src);
    setHeroPreview(null);
  }, [heroPreview]);

  // ── Popup ──
  const handlePopupFileChange = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setPopupFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPopupPreview(ev.target.result);
    reader.readAsDataURL(file);
  };
  const handlePopupUpload = async () => {
    if (!popupFile) { toast.error('Please select an image'); return; }
    setSavingPopup(true);
    try {
      const fd = new FormData(); fd.append('image', popupFile);
      const res = await settingsAPI.updatePopupBanner(fd);
      const url = res.data?.data?.settings?.popupBanner?.url;
      if (url) setPopupBannerUrl(url);
      setPopupFile(null); setPopupPreview(null);
      toast.success('Popup banner updated');
    } catch (err) { toast.error(err.response?.data?.message || 'Upload failed'); }
    finally { setSavingPopup(false); }
  };
  const handlePopupRemove = async () => {
    if (!window.confirm('Remove the popup banner?')) return;
    setRemovingPopup(true);
    try {
      await settingsAPI.removePopupBanner();
      setPopupBannerUrl(''); setPopupFile(null); setPopupPreview(null);
      toast.success('Popup banner removed');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to remove'); }
    finally { setRemovingPopup(false); }
  };

  // ── Facilities ──
  const handleFacilityImagesUpload = async (key, fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setFacilityUploading((p) => ({ ...p, [key]: true }));
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('images', f));
      const res = await settingsAPI.addFacilityImages(key, fd);
      const list = res.data?.data?.settings?.facilities || [];
      setFacilities(list);
      toast.success('Photos uploaded');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setFacilityUploading((p) => ({ ...p, [key]: false }));
    }
  };
  const handleFacilityImageRemove = async (key, publicId) => {
    if (!window.confirm('Remove this photo?')) return;
    const dKey = `${key}:${publicId}`;
    setFacilityDeleting((p) => ({ ...p, [dKey]: true }));
    try {
      const res = await settingsAPI.removeFacilityImage(key, publicId);
      const list = res.data?.data?.settings?.facilities || [];
      setFacilities(list);
      toast.success('Photo removed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove');
    } finally {
      setFacilityDeleting((p) => ({ ...p, [dKey]: false }));
    }
  };

  const saveFacilitiesList = async (nextList) => {
    const res = await settingsAPI.updateFacilities({
      facilities: nextList.map((f) => ({
        key: f.key, title: f.title, description: f.description, icon: f.icon, color: f.color,
      })),
    });
    setFacilities(res.data?.data?.settings?.facilities || []);
  };
  const handleFacilityAdd = async (form) => {
    try {
      const key = makeUniqueKey(form.title, facilities.map((f) => f.key));
      await saveFacilitiesList([...facilities, { ...form, key }]);
      toast.success('Facility added');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add facility');
      throw err;
    }
  };
  const handleFacilityEdit = async (idx, form) => {
    try {
      const next = facilities.map((f, i) => (i === idx ? { ...f, title: form.title, description: form.description, icon: form.icon, color: form.color } : f));
      await saveFacilitiesList(next);
      toast.success('Facility updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update facility');
      throw err;
    }
  };
  const handleFacilityRemove = async (idx) => {
    try {
      const next = facilities.filter((_, i) => i !== idx);
      await saveFacilitiesList(next);
      toast.success('Facility removed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove facility');
    }
  };

  // ── Achievements ──
  const handleAchievementImagesUpload = async (key, fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setAchievementUploading((p) => ({ ...p, [key]: true }));
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('images', f));
      const res = await settingsAPI.addAchievementImages(key, fd);
      setAchievements(res.data?.data?.settings?.achievements || []);
      toast.success('Photos uploaded');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setAchievementUploading((p) => ({ ...p, [key]: false }));
    }
  };
  const handleAchievementImageRemove = async (key, imageId) => {
    if (!window.confirm('Remove this photo?')) return;
    const dKey = imageId;
    setAchievementDeleting((p) => ({ ...p, [dKey]: true }));
    try {
      const res = await settingsAPI.removeAchievementImage(key, imageId);
      setAchievements(res.data?.data?.settings?.achievements || []);
      toast.success('Photo removed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove');
    } finally {
      setAchievementDeleting((p) => ({ ...p, [dKey]: false }));
    }
  };
  const handleAchievementCaptionSave = async (key, imageId, caption) => {
    try {
      const res = await settingsAPI.updateAchievementImageCaption(key, imageId, caption);
      setAchievements(res.data?.data?.settings?.achievements || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save caption');
    }
  };

  const saveAchievementsList = async (nextList) => {
    const res = await settingsAPI.updateAchievements({
      achievements: nextList.map((a) => ({ key: a.key, title: a.title, desc: a.desc })),
    });
    setAchievements(res.data?.data?.settings?.achievements || []);
  };
  const handleAchievementAdd = async (form) => {
    try {
      const key = makeUniqueKey(form.title, achievements.map((a) => a.key));
      await saveAchievementsList([...achievements, { ...form, key }]);
      toast.success('Achievement added');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add achievement');
      throw err;
    }
  };
  const handleAchievementEdit = async (idx, form) => {
    try {
      const next = achievements.map((a, i) => (i === idx ? { ...a, title: form.title, desc: form.desc } : a));
      await saveAchievementsList(next);
      toast.success('Achievement updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update achievement');
      throw err;
    }
  };
  const handleAchievementRemove = async (idx) => {
    try {
      const next = achievements.filter((_, i) => i !== idx);
      await saveAchievementsList(next);
      toast.success('Achievement removed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove achievement');
    }
  };

  // ── Content saves ──
  const saveTestimonials = async () => {
    setSavingTestimonials(true);
    try {
      await settingsAPI.updateTestimonials({ testimonials });
      toast.success('Testimonials saved');
      await loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSavingTestimonials(false); }
  };
  const saveLeadership = async () => {
    setSavingLeadership(true);
    try {
      await settingsAPI.updateLeadership({ leadership });
      toast.success('Leadership team saved');
      await loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSavingLeadership(false); }
  };
  const saveFees = async () => {
    setSavingFees(true);
    try {
      await settingsAPI.updateFeeStructure({ feeStructure });
      toast.success('Fee structure saved');
      await loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSavingFees(false); }
  };
  const savePrograms = async () => {
    setSavingPrograms(true);
    try {
      await settingsAPI.updatePrograms({ programs });
      toast.success('Programs saved');
      await loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSavingPrograms(false); }
  };
  const saveCalendar = async () => {
    setSavingCalendar(true);
    try {
      await settingsAPI.updateAcademicCalendar({ academicCalendar });
      toast.success('Academic calendar saved');
      await loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSavingCalendar(false); }
  };

  // ── Checkerboard style for transparent PNGs ──
  const checkerboard = { background: 'repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%) 0 0 / 20px 20px' };

  if (loading) return (
    <AdminLayout>
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      {/* Page header */}
      <div className="mb-6">
        <h2 className="text-xl sm:text-2xl font-serif font-bold text-primary-500">Site Settings</h2>
        <p className="text-gray-500 text-sm mt-1">Manage website content and appearance. Changes are saved per section.</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">

        {/* ── Desktop sidebar ── */}
        <aside className="hidden lg:block w-56 flex-shrink-0 sticky top-6">
          <nav className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
            {NAV_GROUPS.map((group, gi) => (
              <div key={group.label} className={gi > 0 ? 'border-t border-gray-100' : ''}>
                <p className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-widest">{group.label}</p>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = activeSection === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left transition-colors
                        ${active ? 'bg-primary-50 text-primary-600 font-semibold border-l-2 border-primary-500' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800 border-l-2 border-transparent'}`}
                    >
                      <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-primary-500' : 'text-gray-400'}`} />
                      <span className="flex-1">{item.label}</span>
                    </button>
                  );
                })}
                {gi === NAV_GROUPS.length - 1 && <div className="pb-1" />}
              </div>
            ))}
          </nav>
          <p className="mt-4 text-xs text-gray-400 leading-relaxed px-1">
            Click <span className="font-medium text-gray-500">Save Changes</span> after editing each section.
          </p>
        </aside>

        {/* ── Content area ── */}
        <div className="flex-1 min-w-0 w-full">

          {/* Mobile dropdown nav */}
          <MobileNav activeSection={activeSection} onSelect={setActiveSection} />

          {/* Desktop breadcrumb */}
          <div className="hidden lg:block">
            <SectionBreadcrumb activeSection={activeSection} />
          </div>

          {/* ══ School Logo ══ */}
          {activeSection === 'logo' && (
            <Card>
              <CardHeader
                title="School Logo"
                subtitle="Displayed in the navbar on all pages. PNG with transparent background recommended."
              />

              {/* Navbar mockup preview */}
              <div className="mb-6 rounded-xl overflow-hidden border border-gray-200">
                <div className="bg-primary-600 px-4 py-3 flex items-center gap-3">
                  {(logoPreview || logoUrl) ? (
                    <div className="flex items-center justify-center bg-white/95 rounded-2xl p-1.5 shadow-lg shrink-0">
                      <img src={logoPreview || logoUrl} alt="Logo preview" className="h-10 sm:h-14 w-auto max-w-[140px] object-contain block" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-white/15 ring-1 ring-white/25 flex items-center justify-center shrink-0">
                      <FiImage className="w-5 h-5 sm:w-6 sm:h-6 text-white/60" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-serif font-bold text-white text-xs sm:text-sm leading-tight truncate">Kalauni Kantipur English School</p>
                    <p className="text-white/75 text-[9px] sm:text-[10px] uppercase tracking-widest font-semibold">Inspiring Excellence</p>
                  </div>
                  <div className="ml-auto hidden sm:flex items-center gap-2">
                    <div className="h-7 w-14 rounded-lg bg-white/10" />
                    <div className="h-7 w-14 rounded-lg bg-white/10" />
                    <div className="h-8 w-18 rounded-xl bg-accent-400/80" />
                  </div>
                </div>
                <p className="text-center text-xs text-gray-400 py-1.5 bg-gray-50">Navbar preview</p>
              </div>

              {/* Current logo */}
              {logoUrl && !logoPreview && (
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Current Logo</p>
                    <button onClick={handleLogoRemove} disabled={removingLogo}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-60 transition-colors">
                      {removingLogo ? <Spinner color="red" /> : <FiTrash2 className="w-3 h-3" />}
                      {removingLogo ? 'Removing…' : 'Remove Logo'}
                    </button>
                  </div>
                  <div className="flex items-center justify-center h-48 sm:h-64 rounded-2xl border border-gray-200" style={checkerboard}>
                    <img src={logoUrl} alt="Current Logo" className="max-h-40 sm:max-h-52 max-w-full w-auto object-contain drop-shadow-md" />
                  </div>
                  <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                    <FiCheck className="w-3.5 h-3.5" /> Logo is active in the navbar.
                  </p>
                </div>
              )}

              {/* Preview new */}
              {logoPreview ? (
                <div className="relative mb-5">
                  <div className="flex items-center justify-center h-48 sm:h-64 rounded-2xl border border-gray-200" style={checkerboard}>
                    <img src={logoPreview} alt="Preview" className="max-h-40 sm:max-h-52 max-w-full w-auto object-contain drop-shadow-md" />
                  </div>
                  <button onClick={() => { setLogoPreview(null); setLogoFile(null); }}
                    className="absolute top-3 right-3 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 shadow transition-colors">
                    <FiX className="w-4 h-4" />
                  </button>
                  <p className="text-xs text-gray-400 text-center mt-2">New logo — not saved yet</p>
                </div>
              ) : (
                <DropZone
                  onChange={handleLogoFileChange}
                  accept="image/*"
                  icon={FiImage}
                  label={logoUrl ? 'Click to replace logo' : 'Click to upload school logo'}
                  hint="PNG (transparent bg recommended) · JPG · WebP · up to 5MB"
                  className="mb-5"
                />
              )}

              {logoFile && (
                <button onClick={handleLogoUpload} disabled={savingLogo}
                  className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 disabled:opacity-70 transition-colors text-sm shadow-sm mt-4">
                  {savingLogo ? <Spinner size="md" /> : <FiUpload className="w-4 h-4" />}
                  {savingLogo ? 'Uploading…' : 'Save Logo'}
                </button>
              )}
            </Card>
          )}

          {/* ══ Hero Background ══ */}
          {activeSection === 'hero' && (
            <Card>
              <CardHeader title="Hero Background" subtitle="Upload a photo or intro video shown behind the main headline." />

              {/* Current media */}
              {heroMedia.mediaType !== 'none' && !heroPreview && (
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">
                      Current {heroMedia.mediaType === 'video' ? 'Video' : 'Image'}
                    </p>
                    <button onClick={handleHeroRemove} disabled={removingHero}
                      className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 font-medium disabled:opacity-60 transition-colors">
                      {removingHero ? <Spinner size="sm" color="red" /> : <FiTrash2 className="w-3 h-3" />}
                      {removingHero ? 'Removing…' : 'Remove'}
                    </button>
                  </div>
                  <div className="w-full h-44 sm:h-56 rounded-2xl overflow-hidden border border-gray-200 bg-black">
                    {heroMedia.mediaType === 'video'
                      ? <video src={heroMedia.url} className="w-full h-full object-cover" controls muted />
                      : <img src={heroMedia.url} alt="Hero" className="w-full h-full object-cover" />}
                  </div>
                </div>
              )}

              {/* Type toggle */}
              <div className="flex gap-2 mb-4">
                {['image', 'video'].map((t) => (
                  <button key={t} onClick={() => { setHeroMediaType(t); clearHeroSelection(); }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-colors
                      ${heroMediaType === t ? 'bg-primary-500 text-white border-primary-500 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300'}`}>
                    {t === 'video' ? <FiVideo className="w-4 h-4" /> : <FiImage className="w-4 h-4" />}
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>

              {/* Preview new */}
              {heroPreview ? (
                <div className="mb-4">
                  <ImagePreviewBox
                    src={heroPreview.src}
                    alt="Preview"
                    isVideo={heroPreview.type === 'video'}
                    onClear={clearHeroSelection}
                    label={`New ${heroPreview.type} — not saved yet`}
                    height="h-44 sm:h-56"
                  />
                </div>
              ) : (
                <DropZone
                  key={heroMediaType}
                  onChange={handleHeroFileChange}
                  accept={heroMediaType === 'video' ? 'video/mp4,video/webm,video/ogg' : 'image/*'}
                  icon={heroMediaType === 'video' ? FiVideo : FiImage}
                  label={`Click to select ${heroMediaType}`}
                  hint={heroMediaType === 'video' ? 'MP4, WebM up to 100MB' : 'JPG, PNG, WebP up to 5MB'}
                />
              )}

              {heroFile && (
                <button onClick={handleHeroUpload} disabled={savingHero}
                  className="mt-4 flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 disabled:opacity-70 transition-colors text-sm shadow-sm">
                  {savingHero ? <Spinner size="md" /> : <FiUpload className="w-4 h-4" />}
                  {savingHero ? 'Uploading…' : `Save ${heroMediaType === 'video' ? 'Video' : 'Image'}`}
                </button>
              )}
            </Card>
          )}

          {/* ══ Campus Photo ══ */}
          {activeSection === 'campus' && (
            <Card>
              <CardHeader title="Campus Photo" subtitle="Displayed in the About section on the homepage alongside school info." />

              {campusImageUrl && !campusPreview && (
                <div className="mb-4">
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-2">Current Photo</p>
                  <div className="w-full h-44 sm:h-56 rounded-2xl overflow-hidden border border-gray-200">
                    <img src={campusImageUrl} alt="Campus" className="w-full h-full object-cover" />
                  </div>
                </div>
              )}

              {campusPreview ? (
                <div className="mb-4">
                  <ImagePreviewBox src={campusPreview} alt="Preview" onClear={() => { setCampusPreview(null); setCampusFile(null); }} height="h-44 sm:h-56" />
                </div>
              ) : (
                <DropZone onChange={handleCampusFileChange} accept="image/*" label="Click to upload campus photo" hint="JPG, PNG, WebP up to 5MB" />
              )}

              {campusFile && (
                <button onClick={handleCampusUpload} disabled={savingCampus}
                  className="mt-4 flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 disabled:opacity-70 transition-colors text-sm shadow-sm">
                  {savingCampus ? <Spinner size="md" /> : <FiUpload className="w-4 h-4" />}
                  {savingCampus ? 'Uploading…' : 'Save Campus Photo'}
                </button>
              )}
            </Card>
          )}

          {/* ══ Popup Banner ══ */}
          {activeSection === 'popup' && (
            <Card>
              <CardHeader title="Popup Banner" subtitle="Appears when visitors open or refresh any public page. Remove to disable." />

              {popupBannerUrl && !popupPreview && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Current Banner</p>
                    <button onClick={handlePopupRemove} disabled={removingPopup}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-60 transition-colors">
                      {removingPopup ? <Spinner color="red" /> : <FiTrash2 className="w-3 h-3" />}
                      {removingPopup ? 'Removing…' : 'Remove Banner'}
                    </button>
                  </div>
                  <div className="w-full rounded-2xl overflow-hidden border border-gray-200">
                    <img src={popupBannerUrl} alt="Popup Banner" className="w-full h-auto object-contain" />
                  </div>
                  <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                    <FiCheck className="w-3.5 h-3.5" /> Banner is active — visitors will see this popup on page load.
                  </p>
                </div>
              )}

              {popupPreview ? (
                <div className="mb-4 relative w-full rounded-2xl overflow-hidden border border-gray-200">
                  <img src={popupPreview} alt="Preview" className="w-full h-auto object-contain" />
                  <button onClick={() => { setPopupPreview(null); setPopupFile(null); }}
                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 shadow transition-colors">
                    <FiX className="w-3.5 h-3.5" />
                  </button>
                  <p className="text-xs text-gray-400 text-center py-2 bg-gray-50">New image — not saved yet</p>
                </div>
              ) : (
                <DropZone
                  onChange={handlePopupFileChange}
                  accept="image/*"
                  label={popupBannerUrl ? 'Click to replace banner image' : 'Click to upload banner image'}
                  hint="JPG, PNG, WebP up to 5MB"
                />
              )}

              {popupFile && (
                <button onClick={handlePopupUpload} disabled={savingPopup}
                  className="mt-4 flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 disabled:opacity-70 transition-colors text-sm shadow-sm">
                  {savingPopup ? <Spinner size="md" /> : <FiUpload className="w-4 h-4" />}
                  {savingPopup ? 'Uploading…' : 'Save Popup Banner'}
                </button>
              )}
            </Card>
          )}

          {/* ══ Testimonials ══ */}
          {activeSection === 'testimonials' && (
            <CrudSection
              title="Testimonials"
              subtitle="Shown in the 'What People Say' carousel on the homepage."
              items={testimonials}
              setItems={setTestimonials}
              emptyItem={emptyTestimonial}
              saving={savingTestimonials}
              onSave={saveTestimonials}
              fields={[
                { key: 'name',   label: 'Name',   required: true, placeholder: 'e.g. Anjali Sharma' },
                { key: 'role',   label: 'Role',   required: true, placeholder: 'e.g. Parent of Grade 8 Student' },
                { key: 'rating', label: 'Rating', type: 'stars' },
                { key: 'text',   label: 'Quote',  required: true, type: 'textarea', placeholder: 'Their testimonial…' },
              ]}
            />
          )}

          {/* ══ Facilities ══ */}
          {activeSection === 'facilities' && (
            <FacilitiesManager
              facilities={facilities}
              onAdd={handleFacilityAdd}
              onEdit={handleFacilityEdit}
              onRemove={handleFacilityRemove}
              facilityUploading={facilityUploading}
              facilityDeleting={facilityDeleting}
              onUploadImages={handleFacilityImagesUpload}
              onRemoveImage={handleFacilityImageRemove}
            />
          )}

          {/* ══ Leadership Team ══ */}
          {activeSection === 'leadership' && (
            <LeadershipSection
              leadership={leadership}
              setLeadership={setLeadership}
              saving={savingLeadership}
              onSave={saveLeadership}
            />
          )}

          {/* ══ Achievements ══ */}
          {activeSection === 'achievements' && (
            <AchievementsManager
              achievements={achievements}
              onAdd={handleAchievementAdd}
              onEdit={handleAchievementEdit}
              onRemove={handleAchievementRemove}
              achievementUploading={achievementUploading}
              achievementDeleting={achievementDeleting}
              onUploadImages={handleAchievementImagesUpload}
              onRemoveImage={handleAchievementImageRemove}
              onCaptionSave={handleAchievementCaptionSave}
            />
          )}

          {/* ══ Academic Programs ══ */}
          {activeSection === 'programs' && (
            <ProgramsSection programs={programs} setPrograms={setPrograms} saving={savingPrograms} onSave={savePrograms} />
          )}

          {/* ══ Academic Calendar ══ */}
          {activeSection === 'calendar' && (
            <CrudSection
              title="Academic Calendar"
              subtitle="Shown on the Academics page under 'Planning Ahead'."
              items={academicCalendar}
              setItems={setAcademicCalendar}
              emptyItem={emptyCalEvent}
              saving={savingCalendar}
              onSave={saveCalendar}
              fields={[
                { key: 'month', label: 'Month', required: true, placeholder: 'e.g. April' },
                { key: 'event', label: 'Event', required: true, placeholder: 'e.g. New Academic Session Begins' },
              ]}
            />
          )}

          {/* ══ Fee Structure ══ */}
          {activeSection === 'fees' && (
            <CrudSection
              title="Fee Structure"
              subtitle="Shown in the 'Tuition & Fee Structure' table on the Admissions page."
              items={feeStructure}
              setItems={setFeeStructure}
              emptyItem={emptyFee}
              saving={savingFees}
              onSave={saveFees}
              fields={[
                { key: 'level',     label: 'Level',         required: true, placeholder: 'e.g. Primary (Grade 1-5)' },
                { key: 'admission', label: 'Admission Fee',  placeholder: 'e.g. NPR 15,000' },
                { key: 'monthly',   label: 'Monthly Fee',    placeholder: 'e.g. NPR 3,500' },
                { key: 'annual',    label: 'Annual Fee',     placeholder: 'e.g. NPR 8,000' },
              ]}
            />
          )}

        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
